import { useState, type ReactNode } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Input } from "@/components/shadcn/input";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { formatNumberWithSpaces, isExponentKey } from "@/utils/generic";

interface NumberInputProps {
  id?: string;
  value?: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  onClear?: () => void;
  onEmptied?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  step?: number | "any";
  precision?: number;
  /** Accept whole numbers only: decimal/exponent characters are rejected as
   * typed, so "2,5" or "2.5" can never enter the field. */
  integer?: boolean;
  allowNegative?: boolean;
  isRequired?: boolean;
  prefix?: string;
  suffix?: string;
  formatNumber?: boolean;
  helperText?: ReactNode;
  autoFocus?: boolean;
}

interface NumberInputFieldProps {
  name: string;
  min?: number;
  max?: number;
  label: string;
  tooltip?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  isDisabled?: boolean;
  defaultValue?: number;
  step?: number | "any";
  integer?: boolean;
  precision?: number;
  prefix?: string;
  suffix?: string;
  formatNumber?: boolean;
  helperText?: ReactNode;
  /** Show the required marker. Defaults to whether `rules.required` is set;
   *  pass it when the requirement lives in a `validate` rule instead. */
  isRequired?: boolean;
}

export const NumberInput = ({
  id,
  value,
  min,
  max,
  onChange,
  onClear,
  onEmptied,
  onBlur,
  onKeyDown,
  isDisabled = false,
  placeholder,
  label,
  tooltip,
  step = 1,
  precision,
  integer = false,
  allowNegative,
  isRequired,
  prefix,
  suffix,
  formatNumber,
  helperText,
  autoFocus,
}: NumberInputProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const getPrecisionFromStep = (value: number): number => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const str = value.toString().toLowerCase();
    const expMatch = str.match(/^(\d*\.?\d+)e-(\d+)$/);
    if (expMatch) {
      const coefficient = expMatch[1];
      const exponent = Number.parseInt(expMatch[2], 10);
      const coefficientDecimals = coefficient.includes(".")
        ? coefficient.split(".")[1].length
        : 0;
      return exponent + coefficientDecimals;
    }
    if (!str.includes(".")) return 0;
    return str.split(".")[1].length;
  };

  const calculatedPrecision =
    step === "any"
      ? undefined
      : precision !== undefined
        ? precision
        : getPrecisionFromStep(typeof step === "number" ? step : 1);

  const formatValue = (val: number | undefined): string => {
    if (val == null || !Number.isFinite(val)) return "";
    let formatted = "";
    const isSavedDecimal = integer && !Number.isInteger(val);
    if (calculatedPrecision !== undefined && !isSavedDecimal) {
      const base = val.toFixed(calculatedPrecision);
      formatted = formatNumber ? formatNumberWithSpaces(base) : base;
    } else {
      const base = val.toString();
      formatted = formatNumber ? formatNumberWithSpaces(base) : base;
    }

    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  // Use editValue when editing, otherwise use formatted value
  const displayValue = isEditing ? editValue : formatValue(value);
  const showClear = !!onClear && value != null && !isDisabled;

  // Helper function to round to precision
  const roundToPrecision = (val: number): number => {
    if (calculatedPrecision !== undefined) {
      return parseFloat(val.toFixed(calculatedPrecision));
    }
    return val;
  };

  const allowsNegative = allowNegative ?? (min == null || min < 0);
  const floor = min ?? (allowsNegative ? undefined : 0);
  const typablePattern = integer
    ? allowsNegative
      ? /^-?\d*$/
      : /^\d*$/
    : allowsNegative
      ? /^-?[\d.,\s]*$/
      : /^[\d.,\s]*$/;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const bare = suffix ? raw.replace(new RegExp(`\\s*${suffix}$`), "") : raw;
    if (!typablePattern.test(bare)) return;
    setEditValue(raw);
    setIsEditing(true);

    if (bare.trim() === "") {
      onEmptied?.();
      return;
    }

    const sanitized = bare.replace(/\s|,/g, "");
    const numValue = parseFloat(sanitized);
    if (!isNaN(numValue)) {
      const roundedValue = roundToPrecision(numValue);
      onChange(roundedValue);
    }
  };

  // When step is "any", the chevrons nudge the least-significant digit the value
  // currently shows (2.34 → 2.35 / 2.33, 2 → 3), rounding to that precision so
  // floating-point drift can't creep in. A fixed step is used verbatim.
  const applyStep = (dir: 1 | -1): number => {
    const base = value ?? floor ?? 0;
    if (step !== "any") return roundToPrecision(base + dir * step);
    const shown = isEditing ? editValue : value != null ? String(value) : "";
    const decimals = shown.includes(".") ? shown.split(".")[1].length : 0;
    return parseFloat((base + dir * 10 ** -decimals).toFixed(decimals));
  };

  const handleIncrement = () => {
    if (isDisabled) return;
    const newValue = applyStep(1);

    if (floor != null && newValue < floor) {
      onChange(floor);
      setIsEditing(false);
      return;
    }

    if (max == null || newValue <= max) {
      onChange(newValue);
      setIsEditing(false);
    }
  };

  const handleDecrement = () => {
    if (isDisabled) return;
    const newValue = applyStep(-1);

    if (max != null && newValue > max) {
      onChange(max);
      setIsEditing(false);
      return;
    }

    if (floor == null || newValue >= floor) {
      onChange(newValue);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    if (!editValue) {
      setIsEditing(false);
      onBlur?.();
      return;
    }
    let valueToParse = editValue;
    // Remove suffix if present before parsing
    if (suffix) {
      valueToParse = valueToParse.replace(new RegExp(`\\s*${suffix}$`), "");
    }

    const sanitized = valueToParse.replace(/\s|,/g, "");
    const numValue = parseFloat(sanitized);
    if (isNaN(numValue)) {
      setIsEditing(false);
    } else {
      const roundedValue = roundToPrecision(numValue);
      const clampedValue = Math.max(
        floor ?? -Infinity,
        Math.min(max ?? Infinity, roundedValue)
      );
      onChange(clampedValue);
      setIsEditing(false);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (
      isExponentKey(e.key) ||
      (e.key === "-" && !allowsNegative) ||
      (integer && (e.key === "." || e.key === ","))
    ) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          isRequired={isRequired}
        />
      )}
      <div className="relative">
        <Input
          id={id}
          aria-label={label}
          type={formatNumber || suffix ? "text" : "number"}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={isDisabled}
          autoFocus={autoFocus}
          className={cn(
            "truncate bg-white [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]",
            prefix ? "pl-6" : "",
            showClear ? "pr-14" : "pr-8"
          )}
        />
        {prefix && (
          <span
            aria-hidden
            className="absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground select-none"
          >
            {prefix}
          </span>
        )}
        {showClear && (
          <button
            type="button"
            onClick={onClear}
            className={cn(
              "absolute right-6 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded",
              "text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            )}
            aria-label="Clear"
            title="Clear"
          >
            <X size={13} />
          </button>
        )}
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
          <button
            type="button"
            tabIndex={-1}
            onClick={handleIncrement}
            disabled={
              isDisabled ||
              (max !== undefined && value !== undefined && value >= max)
            }
            className={cn(
              "flex h-3 w-4 items-center justify-center text-muted-foreground transition-colors hover:text-foreground",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-muted-foreground"
            )}
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            onClick={handleDecrement}
            disabled={
              isDisabled ||
              (floor != null && value !== undefined && value <= floor)
            }
            className={cn(
              "flex h-3 w-4 items-center justify-center text-muted-foreground transition-colors hover:text-foreground",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-muted-foreground"
            )}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
      {helperText && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}
    </div>
  );
};

export const NumberInputField = ({
  name,
  min,
  max,
  label,
  tooltip,
  control,
  isDisabled,
  rules,
  helperText,
  isRequired,
  defaultValue,
  step,
  integer,
  precision,
  prefix,
  suffix,
  formatNumber,
}: NumberInputFieldProps) => {
  return (
    <Controller
      control={control}
      disabled={isDisabled}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }) => {
        // field.value may arrive as a string; coerce so .toFixed() can't throw.
        const numeric =
          typeof field.value === "number"
            ? field.value
            : parseFloat(field.value);
        const value = Number.isFinite(numeric) ? numeric : undefined;
        return (
          <NumberInput
            value={value}
            min={min}
            max={max}
            onChange={field.onChange}
            onClear={
              (isRequired ?? !!rules?.required)
                ? undefined
                : () => field.onChange("")
            }
            onEmptied={() => field.onChange(min ?? 0)}
            onBlur={field.onBlur}
            isDisabled={isDisabled}
            label={label}
            tooltip={tooltip}
            step={step}
            integer={integer}
            precision={precision}
            isRequired={isRequired ?? !!rules?.required}
            prefix={prefix}
            suffix={suffix}
            formatNumber={formatNumber}
            helperText={helperText}
          />
        );
      }}
      name={name}
    />
  );
};

// Keep the old component for backward compatibility
export const NoxusNumberInput = NumberInput;
